// src/components/payment/PaymentMethodForm.jsx
// Consolidated Payment Method Form & Verification component.
// Replaces duplication across AddPaymentMethodModal, AddPaymentMethodModalInline, and WithdrawModal.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, AlertCircle, AlertTriangle, Shield, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import paymentService from '../../services/paymentService';

const MOBILE_NETWORKS = ['MTN', 'Vodafone', 'AirtelTigo', 'Telecel', 'Tigo'];

/**
 * PaymentMethodForm Props:
 * @param {boolean}  [isOpen=true]          - Whether the modal is visible (when !isInline)
 * @param {Function} [onClose]              - Called on explicit close/dismiss
 * @param {Function} [onSuccess]            - Called after verification or manual review submission
 * @param {boolean}  [isInline=false]       - Render inline without modal backdrop
 * @param {boolean}  [showSuccessStep=true] - Show step 4 success screen or complete immediately
 * @param {Function} [onVerified]          - Callback on verification with { methodId, updatedMethods }
 * @param {Function} [onManualSubmitted]   - Callback on manual review with updatedMethods
 * @param {boolean}  [isOnboardingFlow=false] - For onboarding flows
 * @param {Function} [onGoBackToNameStep]  - Callback when clicking update name in onboarding/inline
 * @param {boolean}  [reloadOnSuccess=false] - Deprecated legacy reload flag (logs warning)
 */
const PaymentMethodForm = ({
  isOpen = true,
  onClose,
  onSuccess,
  isInline = false,
  showSuccessStep = true,
  onVerified,
  onManualSubmitted,
  isOnboardingFlow = false,
  onGoBackToNameStep,
  reloadOnSuccess = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading]                   = useState(false);
  const [step, setStep]                         = useState(1); // 1: form, 2: requiresManual, 3: NAME_MISMATCH, 4: success
  const [type, setType]                         = useState('mobile_money');
  const [details, setDetails]                   = useState({ bankCode: '', bankName: '', accountNumber: '' });
  const [manualReviewData, setManualReviewData] = useState(null);
  const [userNotes, setUserNotes]               = useState('');
  const [banks, setBanks]                       = useState([]);

  // Fetch banks gated by isOpen or when rendered inline (BUG-3 resolution)
  useEffect(() => {
    if (isInline || isOpen) {
      fetchBanks();
    }
  }, [isOpen, isInline]);

  const fetchBanks = async () => {
    try {
      const res = await paymentService.getBanks();
      if (res?.success && Array.isArray(res.data)) {
        setBanks(res.data);
      }
    } catch (err) {
      console.error('Failed to fetch banks', err);
    }
  };

  const handleClose = () => {
    setStep(1);
    setDetails({ bankCode: '', bankName: '', accountNumber: '' });
    setManualReviewData(null);
    setUserNotes('');
    if (onClose) onClose();
  };

  const handleTypeChange = (newType) => {
    setType(newType);
    // BUG-2 fix: always reset details on type switch
    setDetails({ bankCode: '', bankName: '', accountNumber: '' });
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();

    if (!details.bankCode || !details.accountNumber) {
      toast.error(t('dashboard.wallet.enter_all_details', { defaultValue: 'Please enter all payment details' }));
      return;
    }

    setLoading(true);
    try {
      const res = await paymentService.verifyPaymentMethod({
        type,
        bankCode: details.bankCode,
        accountNumber: details.accountNumber,
      });

      if (res?.success) {
        if (reloadOnSuccess) {
          console.warn('[PaymentMethodForm] reloadOnSuccess is deprecated and will be removed in a future release.');
        }

        let updatedMethods = [];
        try {
          const methodsRes = await paymentService.getPaymentMethods();
          updatedMethods = methodsRes?.data || [];
        } catch (err) {
          console.error('Failed to fetch updated payment methods', err);
        }

        const newMethod = updatedMethods.find(
          (m) => m.identifier === details.accountNumber && m.type === type
        );
        const methodId = newMethod?.id || newMethod?._id;

        if (showSuccessStep) {
          setStep(4);
          setTimeout(() => {
            if (onVerified) onVerified({ methodId, updatedMethods });
            if (onSuccess) onSuccess(updatedMethods);
            if (!isOnboardingFlow && !isInline) {
              handleClose();
            }
          }, 2500);
        } else {
          toast.success(t('dashboard.wallet.verified', { defaultValue: 'Verified' }));
          if (onVerified) onVerified({ methodId, updatedMethods });
          if (onSuccess) onSuccess(updatedMethods);
          if (!isInline) {
            handleClose();
          }
        }
      } else if (res?.requiresManual) {
        setManualReviewData({
          details: { ...details, type },
          resolvedName: res.resolvedName,
          message: res.message,
        });
        setStep(2);
      } else {
        toast.error(res?.message || 'Verification failed');
      }
    } catch (error) {
      if (error?.errorCode === 'NAME_MISMATCH') {
        // BUG-1 fix: always populate manualReviewData.details
        // BUG-4 fix: always step 3
        setManualReviewData({
          details: { ...details, type },
          resolvedName: error.accountName,
          message: error.message,
        });
        setStep(3);
      } else {
        toast.error(error?.message || 'Verification failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleManualSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        methodDetails: manualReviewData?.details || { ...details, type },
        resolvedName: manualReviewData?.resolvedName,
        userNotes,
      };
      const res = await paymentService.submitManualMethodVerification(payload);
      if (res?.success) {
        toast.success(
          t('wallet.manual_review_submitted', {
            defaultValue: 'Submitted for manual review — we will notify you by email.',
          })
        );
        let updatedMethods = [];
        try {
          const methodsRes = await paymentService.getPaymentMethods();
          updatedMethods = methodsRes?.data || [];
        } catch (err) {
          console.error('Failed to fetch updated payment methods', err);
        }
        if (onManualSubmitted) {
          onManualSubmitted(updatedMethods);
        }
        if (onSuccess) {
          onSuccess(updatedMethods);
        }
        handleClose();
      } else {
        toast.error(res?.message || 'Submission failed');
      }
    } catch (error) {
      toast.error(error?.message || 'Error submitting for review');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAccountName = () => {
    if (isOnboardingFlow && onGoBackToNameStep) {
      handleClose();
      onGoBackToNameStep();
    } else if (isInline) {
      if (onGoBackToNameStep) {
        handleClose();
        onGoBackToNameStep();
      } else {
        toast(
          t('wallet.update_account_name_notice', {
            defaultValue: 'Please update your profile name in Account Settings to match your payment account.',
          }),
          { icon: 'ℹ️' }
        );
      }
    } else {
      handleClose();
      navigate('/dashboard/settings?tab=account');
    }
  };

  // Filter and deduplicate banks by type
  const filteredBanks = (() => {
    const filtered = banks.filter((bank) => {
      const isMomo =
        bank.type === 'mobile_money' ||
        MOBILE_NETWORKS.some((n) => bank.name?.toLowerCase().includes(n.toLowerCase()));
      return type === 'mobile_money' ? isMomo : !isMomo;
    });
    const seen = new Set();
    return filtered
      .filter((b) => {
        const id = b.code || b.name;
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  })();

  if (!isInline && !isOpen) return null;

  const formContent = (
    <div className={isInline ? 'w-full' : 'bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden'}>
      {/* Header (Modal or Inline with Close) */}
      {!isInline ? (
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <Shield className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">
                {step === 1 && t('wallet.add_payment_method_title', 'Add Payment Method')}
                {step === 2 && t('wallet.manual_review_title', 'Manual Review Needed')}
                {step === 3 && t('wallet.name_mismatch_title', 'Name Mismatch Detected')}
                {step === 4 && t('wallet.success_title', 'Success')}
              </h3>
              {step === 1 && (
                <p className="text-xs text-gray-500">
                  {t('wallet.secondary_verification_desc', 'Alternative to ID upload — same Secondary Verification result')}
                </p>
              )}
            </div>
          </div>
          {onClose && (
            <button
              type="button"
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      ) : null}

      <div className={isInline ? 'py-2' : 'p-6'}>
        {/* Step 1: Verification Form */}
        {step === 1 && (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Type selector */}
            <div>
              <label htmlFor="payment-method-type" className="block text-sm font-medium text-gray-700 mb-1">
                {t('wallet.method_type', 'Payment Type')}
              </label>
              <select
                id="payment-method-type"
                value={type}
                onChange={(e) => handleTypeChange(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="mobile_money">{t('wallet.mobile_money', 'Mobile Money')}</option>
                <option value="bank_transfer">{t('wallet.bank_transfer', 'Bank Transfer')}</option>
              </select>
            </div>

            {/* Network / Bank selector */}
            <div>
              <label htmlFor="payment-method-bank" className="block text-sm font-medium text-gray-700 mb-1">
                {type === 'mobile_money'
                  ? t('dashboard.wallet.network', { defaultValue: 'Network' })
                  : t('dashboard.wallet.bank', { defaultValue: 'Bank' })}
              </label>
              <select
                id="payment-method-bank"
                value={details.bankCode}
                onChange={(e) => {
                  const bank = banks.find((b) => b.code === e.target.value);
                  setDetails({
                    ...details,
                    bankCode: e.target.value,
                    bankName: bank?.name || '',
                  });
                }}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                required
              >
                <option value="">
                  {t('dashboard.wallet.select', { defaultValue: 'Select' })}{' '}
                  {type === 'mobile_money' ? 'Network' : 'Bank'}
                </option>
                {filteredBanks.map((bank) => (
                  <option key={bank.code || bank.name} value={bank.code}>
                    {bank.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Account number */}
            <div>
              <label htmlFor="payment-method-account-number" className="block text-sm font-medium text-gray-700 mb-1">
                {type === 'mobile_money'
                  ? t('wallet.mobile_number', 'Mobile Number')
                  : t('wallet.account_number', 'Account Number')}
              </label>
              <input
                id="payment-method-account-number"
                type="text"
                value={details.accountNumber}
                onChange={(e) => setDetails({ ...details, accountNumber: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                required
                placeholder={
                  type === 'mobile_money'
                    ? '0XX XXX XXXX'
                    : t('wallet.enter_account_number', 'Enter account number')
                }
              />
            </div>

            {/* Info note */}
            <div className="bg-yellow-50 p-3 rounded-lg flex items-start">
              <AlertCircle className="w-4 h-4 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-yellow-700">
                {t(
                  'dashboard.wallet.the_account_name_must_match_your_profile',
                  'The account name must match your profile name exactly to be verified automatically. You do not need to enter your name — the payment processor retrieves it.'
                )}
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || !details.bankCode || !details.accountNumber}
              className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm shadow-sm"
            >
              {loading
                ? t('wallet.verifying', 'Verifying...')
                : t('wallet.verify_and_save', 'Verify & Save')}
            </button>
          </form>
        )}

        {/* Step 2: Name Mismatch — Manual Review Option */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="flex items-center mb-2">
                <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
                <h4 className="font-semibold text-red-800 text-sm">
                  {t('wallet.verification_failed', 'Name Mismatch Detected')}
                </h4>
              </div>
              <p className="text-sm text-red-700 mb-2">
                {t('dashboard.wallet.the_name_on_the_account', { defaultValue: 'The name on the account' })}{' '}
                <strong>({manualReviewData?.resolvedName})</strong>{' '}
                {t('dashboard.wallet.does_not_match_your_profile_name', {
                  defaultValue: 'does not match your profile name.',
                })}
              </p>
              <p className="text-sm text-red-700">
                {t(
                  'dashboard.wallet.you_can_submit_this_for_manual_review_if',
                  'You can submit this for manual review if this is a valid account (e.g., maiden name, business name).'
                )}
              </p>
            </div>

            <div>
              <label htmlFor="payment-method-user-notes" className="block text-sm font-medium text-gray-700 mb-1">
                {t('dashboard.wallet.reason_notes_optional', { defaultValue: 'Reason / Notes (Optional)' })}
              </label>
              <textarea
                id="payment-method-user-notes"
                value={userNotes}
                onChange={(e) => setUserNotes(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                rows={3}
                placeholder={t('wallet.explain_name_difference', 'Explain why the name differs...')}
              />
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="flex-1 py-2 px-4 border rounded-lg hover:bg-gray-50 text-sm transition-colors"
              >
                {t('dashboard.wallet.back', { defaultValue: 'Back' })}
              </button>
              <button
                type="button"
                onClick={handleManualSubmit}
                disabled={loading}
                className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm transition-colors shadow-sm"
              >
                {loading
                  ? t('wallet.submitting', 'Submitting...')
                  : t('wallet.submit_for_review', 'Submit for Review')}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Hard Name Mismatch — Actionable Error Screen */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="bg-red-50 p-4 rounded-lg border border-red-100">
              <div className="flex items-center mb-2">
                <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
                <h4 className="font-semibold text-red-800">
                  {t('wallet.account_name_mismatch_title', 'Account Name Mismatch')}
                </h4>
              </div>
              <p className="text-sm text-red-700 mb-2">
                The {type === 'bank_transfer' ? 'Bank Account' : 'Mobile Money Account'} is registered to{' '}
                <strong>"{manualReviewData?.resolvedName}"</strong>, but this does not match your GigShip profile name.
              </p>
              <p className="text-sm text-red-700">
                {t('wallet.match_exact_notice', 'To proceed, please ensure your profile name matches the account name exactly.')}
              </p>
            </div>

            <div className="flex flex-col space-y-3 mt-6">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="w-full py-2.5 px-4 border border-gray-300 font-medium text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
              >
                {t('wallet.try_again', 'Try Again')}
              </button>
              <button
                type="button"
                onClick={handleUpdateAccountName}
                className="w-full py-2.5 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm text-sm"
              >
                {t('wallet.update_account_name', 'Update Account Name')}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Verification Success Screen */}
        {step === 4 && (
          <div className="flex flex-col items-center justify-center py-8 text-center animate-in fade-in zoom-in duration-300">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-2">
              {t('wallet.verified_and_added', 'Verified & Added!')}
            </h4>
            <p className="text-gray-600 text-sm">
              {t('wallet.ready_to_use', 'Your payment method is now ready to use.')}
            </p>
          </div>
        )}
      </div>
    </div>
  );

  if (isInline) {
    return formContent;
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
      role="dialog"
      aria-modal="true"
    >
      {formContent}
    </div>
  );
};

export default PaymentMethodForm;
